/**
 * Single source of truth for design tokens. Consumed by web/src/theme/index.ts
 * (MUI theme) and any other surface that needs the same visual language.
 *
 * Fonts: Inter (UI/body), Fraunces (display/headings), JetBrains Mono (code).
 */

export const fontFamilies = {
  sans: '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  display: '"Fraunces", "Iowan Old Style", Georgia, serif',
  mono: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
} as const;

export const fontWeights = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

export const fontSizes = {
  xs: "0.75rem",
  sm: "0.875rem",
  md: "1rem",
  lg: "1.125rem",
  xl: "1.25rem",
  "2xl": "1.5rem",
  "3xl": "1.875rem",
  "4xl": "2.25rem",
  "5xl": "3rem",
} as const;

export const lineHeights = {
  tight: 1.15,
  snug: 1.3,
  normal: 1.5,
  relaxed: 1.7,
} as const;

export const radii = {
  none: 0,
  xs: 2,
  sm: 4,
  md: 8,
  lg: 12,
  xl: 16,
  full: 9999,
} as const;

export const spacing = (n: number): number => n * 8;

export const breakpoints = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
} as const;

export const palette = {
  light: {
    bg: "#FAFAF7",
    bgElevated: "#FFFFFF",
    surface: "#FFFFFF",
    border: "#E5E5DD",
    text: "#1C1B17",
    textMuted: "#5A5851",
    primary: "#2D5BFF",
    primaryHover: "#1F47CC",
    primaryContrast: "#FFFFFF",
    accent: "#FF7A45",
    success: "#2E7D32",
    warning: "#ED6C02",
    danger: "#D32F2F",
    info: "#0288D1",
  },
  dark: {
    bg: "#0E0E0C",
    bgElevated: "#161613",
    surface: "#1B1B17",
    border: "#2A2A24",
    text: "#F5F4EE",
    textMuted: "#A8A69D",
    primary: "#5B82FF",
    primaryHover: "#7A9BFF",
    primaryContrast: "#0E0E0C",
    accent: "#FF9A6B",
    success: "#66BB6A",
    warning: "#FFA726",
    danger: "#EF5350",
    info: "#29B6F6",
  },
} as const;

export const shadows = {
  sm: "0 1px 2px rgba(0,0,0,0.06)",
  md: "0 4px 12px rgba(0,0,0,0.08)",
  lg: "0 12px 32px rgba(0,0,0,0.12)",
} as const;

export const tokens = {
  fontFamilies,
  fontWeights,
  fontSizes,
  lineHeights,
  radii,
  spacing,
  breakpoints,
  palette,
  shadows,
} as const;

export type Tokens = typeof tokens;
export type PaletteMode = keyof typeof palette;
